import { rateBasisPoints } from "./alicuotas.js";
import { ROUNDING_TOLERANCE_CENTS, formatCentsAr } from "./money.js";
/**
 * Invoices round IVA per item, so a neto derived from a rate's IVA can sit a few cents off; real
 * files of the previous system (2014–2026) show differences up to about a peso on mixed-rate
 * purchases. Up to this much is assigned to the largest line and flagged; beyond it, rejected.
 */
export const NET_SPLIT_MAX_ADJUSTMENT_CENTS = 100;
const pesos = (cents) => `$${formatCentsAr(cents)}`;
const percent = (rate) => `${rate.replace(".", ",")}%`;
/** round(a / b) for non-negative integers, exact past 2^53 (cents × 10 000 can get there). */
function roundDiv(a, b) {
    return (a * 2n + b) / (b * 2n);
}
/** The neto whose IVA at `rate` is `taxCents`: taxCents × 10 000 / basis points, rounded. */
export function netFromTax(taxCents, rate) {
    return Number(roundDiv(BigInt(taxCents) * 10000n, BigInt(rateBasisPoints(rate))));
}
/** The IVA at `rate` of `netCents`, rounded to the cent. */
export function taxFromNet(netCents, rate) {
    return Number(roundDiv(BigInt(netCents) * BigInt(rateBasisPoints(rate)), 10000n));
}
function largestFirst(lines) {
    let index = 0;
    lines.forEach((line, i) => {
        if (line.netCents > lines[index].netCents)
            index = i;
    });
    return index;
}
/**
 * Purchases: one neto, IVA per rate (10,5 / 21 / 27 in the previous system). One rate with IVA
 * takes the whole neto. Several: each neto is its IVA ÷ rate, and the cents left over go to the
 * largest line when they are within `NET_SPLIT_MAX_ADJUSTMENT_CENTS`. Amounts are non-negative.
 */
export function splitNetByTax(netCents, taxes) {
    const withTax = taxes.filter((t) => t.taxCents !== 0 && rateBasisPoints(t.rate) > 0);
    if (withTax.some((t) => t.taxCents < 0) || netCents < 0) {
        return { ok: false, reason: "el neto y el IVA no pueden ser negativos." };
    }
    if (withTax.length === 0) {
        return {
            ok: false,
            reason: `hay neto gravado de ${pesos(netCents)} sin IVA: si no discrimina IVA, cargalo como no gravado o exento.`,
        };
    }
    if (withTax.length === 1) {
        const only = withTax[0];
        return {
            ok: true,
            lines: [{ rate: only.rate, netCents, taxCents: only.taxCents }],
            adjustmentCents: 0,
            derived: false,
        };
    }
    const lines = withTax.map((t) => ({
        rate: t.rate,
        netCents: netFromTax(t.taxCents, t.rate),
        taxCents: t.taxCents,
    }));
    const derivedSum = lines.reduce((acc, line) => acc + line.netCents, 0);
    const adjustment = netCents - derivedSum;
    if (Math.abs(adjustment) > NET_SPLIT_MAX_ADJUSTMENT_CENTS) {
        return {
            ok: false,
            reason: `el neto gravado (${pesos(netCents)}) no se puede repartir entre alícuotas: según el IVA de ` +
                `cada una, ${lines.map((l) => `${pesos(l.netCents)} al ${percent(l.rate)}`).join(" y ")} ` +
                `suman ${pesos(derivedSum)}. Cargalo con el detalle por alícuota.`,
        };
    }
    const target = lines[largestFirst(lines)];
    target.netCents += adjustment;
    if (lines.some((line) => line.netCents <= 0)) {
        return { ok: false, reason: "el reparto del neto entre alícuotas deja una alícuota sin neto." };
    }
    return { ok: true, lines, adjustmentCents: adjustment, derived: true };
}
/**
 * Sales of the previous system: one neto and one IVA, and only 10,5% and 21% exist there (its
 * sales table has no other rate column). One rate whose IVA matches within rounding takes the
 * whole neto. An IVA strictly between the two is a mix, and with two rates and two equations
 * (neto₁₀,₅ + neto₂₁ = neto; 10,5% neto₁₀,₅ + 21% neto₂₁ = IVA) the split is determined:
 * neto₂₁ = IVA ÷ 10,5% − neto. Anything else is rejected.
 */
export function splitSaleNet(netCents, taxCents) {
    if (netCents < 0 || taxCents < 0) {
        return { ok: false, reason: "el neto y el IVA no pueden ser negativos." };
    }
    if (taxCents === 0) {
        return {
            ok: false,
            reason: `hay neto gravado de ${pesos(netCents)} sin IVA: si es exento o no gravado, va en esa columna.`,
        };
    }
    const at21 = taxFromNet(netCents, "21");
    const at105 = taxFromNet(netCents, "10.5");
    // The rate whose IVA is closest wins when it is the only one within rounding — or, on a voucher
    // so small that both are, when it matches exactly (Codex 016 M1 round 2: $1,00 with $0,21).
    const off21 = Math.abs(at21 - taxCents);
    const off105 = Math.abs(at105 - taxCents);
    const within = (off) => off <= ROUNDING_TOLERANCE_CENTS;
    const single = (rate) => ({
        ok: true,
        lines: [{ rate, netCents, taxCents }],
        adjustmentCents: 0,
        derived: false,
    });
    if (off21 === 0 && off105 !== 0)
        return single("21");
    if (off105 === 0 && off21 !== 0)
        return single("10.5");
    if (within(off21) && !within(off105))
        return single("21");
    if (within(off105) && !within(off21))
        return single("10.5");
    if (taxCents > at105 && taxCents < at21) {
        const net21 = netFromTax(taxCents, "10.5") - netCents;
        const net105 = netCents - net21;
        const tax21 = taxFromNet(net21, "21");
        const tax105 = taxCents - tax21;
        if (net21 > 0 && net105 > 0 && tax105 > 0) {
            return {
                ok: true,
                lines: [
                    { rate: "10.5", netCents: net105, taxCents: tax105 },
                    { rate: "21", netCents: net21, taxCents: tax21 },
                ],
                adjustmentCents: 0,
                derived: true,
            };
        }
    }
    return {
        ok: false,
        reason: `el IVA (${pesos(taxCents)}) no corresponde al 10,5% (${pesos(at105)}) ni al 21% ` +
            `(${pesos(at21)}) del neto ${pesos(netCents)}, ni a una mezcla de los dos.`,
    };
}
//# sourceMappingURL=net-split.js.map